import { FC, FormEvent, ReactElement, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Stack, Typography } from "@mui/material";
import { KeyboardBackspace } from "@mui/icons-material";

import { TextInput } from "@/components";
import { forgot_password_icon } from "@/constants";
import {
  useResetPasswordMutation,
  useUpdateUserPasswordMutation,
} from "@/services";
import { useSnackbar } from "@/hooks";

const SetNewPassword: FC = (): ReactElement => {
  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const navigate = useNavigate();
  const { show } = useSnackbar();
  const [resetPassword, { isLoading }] = useResetPasswordMutation();
  const [onBoardUser, { isLoading: isNewUserLoading }] =
    useUpdateUserPasswordMutation();
  const token = new URLSearchParams(location.search).get("token");
  const email = new URLSearchParams(location.search).get("email");
  const cognitoPassword = new URLSearchParams(location.search).get("password");
  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (token) {
      resetPassword({ newPassword: password, token })
        .unwrap()
        .then((res) => {
          show(
            "Password updated successfully!",
            res?.resetPassword?.data?.msg,
            "success"
          );
          navigate("/reset-password-success");
        })
        .catch((error) => {
          show("Invalid password or confirm password!", error.message, "error");
        });
    } else if (cognitoPassword && email) {
      onBoardUser({
        email,
        newPassword: password,
        newUser: true,
        previousPassword: cognitoPassword,
      })
        .unwrap()
        .then(() => {
          show(
            "Password updated successfully!",
            "User onBoarded successfully",
            "success"
          );
          navigate("/reset-password-success");
        })
        .catch((error) => {
          show("Invalid password or confirm password!", error.message, "error");
        });
    }
  };

  useEffect(() => {
    if (!token && !cognitoPassword && !email) {
      navigate("/");
    }
  }, [token, navigate, email, cognitoPassword]);

  return (
    <Stack
      direction={"column"}
      justifyContent={"center"}
      alignItems={"center"}
      height={"100%"}
    >
      <Box sx={{ width: { xs: "95%", sm: "70%" }, p: { xs: 2, md: 0 } }}>
        <form onSubmit={handleSubmit}>
          <Stack
            spacing={1}
            direction={"column"}
            alignItems={"center"}
            justifyContent={"center"}
          >
            <img src={forgot_password_icon} alt="" width={74} />
            <Box textAlign={"center"}>
              <Typography
                fontSize={{ md: "32px", xs: "22px" }}
                fontWeight={600}
              >
                Reset Password
              </Typography>
              <Typography fontSize={14} color={"neutral.500"}>
                Create a new password for your account by filling out the form
                below
              </Typography>
            </Box>
          </Stack>

          <Box sx={{ my: 2 }}>
            <Stack spacing={2}>
              <Box>
                <TextInput
                  label="New Password"
                  type="password"
                  placeholder="Create new password"
                  color="success"
                  error={false}
                  helperText={""}
                  value={password}
                  handleChangeValue={(val: string) => setPassword(val)}
                />
              </Box>
              <Box>
                <TextInput
                  label="Confirm New Password"
                  type="password"
                  placeholder="Re-enter new password"
                  color="success"
                  error={false}
                  helperText={""}
                  value={confirmPassword}
                  handleChangeValue={(val: string) => setConfirmPassword(val)}
                />
              </Box>
            </Stack>
          </Box>
          <Button
            variant="contained"
            fullWidth
            sx={{
              mt: 2,
              py: 1.5,
              fontSize: "16px",
              fontWeight: 500,
            }}
            disabled={
              isNewUserLoading ||
              isLoading ||
              password === "" ||
              password !== confirmPassword
            }
            type="submit"
            color="primary"
          >
            Change Password
          </Button>

          <Button
            variant="outlined"
            fullWidth
            sx={{
              mt: 2,
              py: 1.5,
              fontSize: "16px",
              fontWeight: 500,
              color: "#030712",
              border: "1px solid #D4D6D9",
            }}
            color="secondary"
            onClick={() => navigate(-1)}
            startIcon={<KeyboardBackspace />}
          >
            Go Back
          </Button>
        </form>
      </Box>
    </Stack>
  );
};

export default SetNewPassword;
